import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mockDramas } from '../../../mocks/dramas';
import { createDramaUrl } from '../../../hooks/useNavigation';

const NewDramasCarousel = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const newDramas = [...mockDramas]
    .sort(
      (a, b) =>
        new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
    )
    .map((drama) => ({
      id: drama.id,
      slug: drama.slug,
      genreSlug: drama.genreSlug,
      title: drama.title,
      poster: drama.poster,
      rating: drama.rating,
      episodes:
        drama.status === 'ongoing'
          ? Math.floor(drama.episodes / 2)
          : drama.episodes,
      totalEpisodes: drama.episodes,
      genre: drama.genre[0],
      releaseDate: new Date(drama.releaseDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      isOngoing: drama.status === 'ongoing',
      country: drama.country,
      views: drama.views,
      description: drama.description,
    }));

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;
        if (isAtEnd) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollBy({ left: 220, behavior: 'smooth' });
        }
        setTimeout(checkScrollButtons, 300);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg shadow-amber-500/20 text-white">
            <i className="ri-sparkling-2-fill text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              New Dramas This Week
            </h2>
            <p className="text-xs text-gray-500">Fresh releases just for you</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Scroll arrows */}
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              canScrollLeft
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              canScrollRight
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
          <Link
            to="/dramas"
            className="ml-1 text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1 whitespace-nowrap cursor-pointer"
          >
            View All
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>

      {/* Carousel */}
      <div
        ref={scrollRef}
        onScroll={checkScrollButtons}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="flex gap-5 overflow-x-auto pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {newDramas.map((drama) => (
          <Link
            key={drama.id}
            to={createDramaUrl(drama.genreSlug, drama.slug)}
            className="flex-shrink-0 w-[200px] group cursor-pointer"
            onMouseEnter={() => setHoveredId(drama.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            {/* Card */}
            <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 hover:shadow-xl hover:border-gray-200 transition-all duration-300 hover:-translate-y-1">
              {/* Poster */}
              <div className="relative w-[200px] h-[280px] overflow-hidden">
                <img
                  src={drama.poster}
                  alt={drama.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                {/* Subtle gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

                {/* NEW badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-lg shadow-lg shadow-amber-500/30 flex items-center gap-1">
                    <i className="ri-sparkling-fill text-[10px]"></i>
                    NEW
                  </span>
                </div>

                {/* Date badge */}
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 bg-black/30 backdrop-blur-md text-white text-[10px] font-medium rounded-lg border border-white/10">
                    {drama.releaseDate}
                  </span>
                </div>

                {/* Bottom overlay info */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-amber-300 text-xs font-semibold">
                      <i className="ri-star-fill text-[11px]"></i>
                      {drama.rating}
                    </span>
                    <span className="text-white/70 text-[10px]">
                      {drama.episodes}/{drama.totalEpisodes} eps
                    </span>
                  </div>
                </div>

                {/* Hover play */}
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-300 ${
                    hoveredId === drama.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-2xl transform transition-transform duration-300 group-hover:scale-100 scale-75">
                    <i className="ri-play-fill text-amber-600 text-xl ml-0.5"></i>
                  </div>
                </div>
              </div>

              {/* Info area */}
              <div className="p-3.5">
                <h3 className="text-sm font-bold text-gray-900 group-hover:text-amber-600 transition-colors truncate">
                  {drama.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-md">
                    {drama.genre}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {drama.country}
                  </span>
                </div>
                {drama.isOngoing && (
                  <div className="mt-2">
                    <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"
                        style={{
                          width: `${(drama.episodes / drama.totalEpisodes) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Ongoing</p>
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default NewDramasCarousel;
